import { useRef } from "react";
import "../../../../../css/FinancialPromotions/Components/FileUpload.css";
import { ReactComponent as UploadIcon } from "../../../../../assets/icons/financialPromotions/File Add (filled).svg";
import UploadedFileList from "./UploadedFileList";
import { MAX_FILES } from "../../utils/fpUtils";

const uniqByNameSize = (arr) => {
  const seen = new Set();
  return arr.filter((f) => {
    const k = `${f.name}__${f.size}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const FileUpload = ({
  label,
  description = "Drag & drop files here or",
  browseText = "Browse",
  status = "default",
  disabled = false,
  numberLimit = MAX_FILES,
  maxFileSize = 20 * 1024 * 1024, // 20 MB

  // new
  files = [],
  onFilesChange,
  accept,
  multiple = true,
}) => {
  const isMaxReached = files.length >= numberLimit;
  const inputRef = useRef(null);
  const addFiles = (newFiles) => {
    if (disabled) return;

    const incoming = Array.from(newFiles || []);
    if (!incoming.length) return;

    onFilesChange?.((prev = []) => {
      const merged = uniqByNameSize([...prev, ...incoming]);

      // enforce limit
      if (merged.length > numberLimit) {
        return merged.slice(0, numberLimit);
      }

      return merged;
    });

    // reset input so same file can be selected again
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleBrowse = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (disabled) return;
    addFiles(event.dataTransfer?.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleRemove = (_file, index) => {
    onFilesChange?.((prev) => (prev || []).filter((_, i) => i !== index));
    if (inputRef.current) inputRef.current.value = ""; // allows re-selecting same file
  };

  return (
    <div className="ds-upload">
      {label && <div className="ds-upload-label">{label}</div>}

      <div
        className={`ds-upload-dropzone ds-upload-dropzone--${status} ${
          disabled || isMaxReached ? "ds-upload-dropzone--disabled" : ""
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleBrowse();
        }}
      >
        <div className="ds-upload-row">
          <div className="ds-upload-icon" aria-hidden>
            <UploadIcon />
          </div>

          <div className="ds-upload-text">
            <div className="Content">{description}</div>
            <button
              type="button"
              className="ds-upload-browse"
              onClick={handleBrowse}
              disabled={disabled || isMaxReached}
            >
              {browseText}
            </button>
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        className="ds-upload-input"
        style={{ display: "none" }}
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        max={numberLimit}
        onChange={(e) => addFiles(e.target.files)}
      />
      {accept && (
        <div className="ds-upload-accept FP_Content">
          <em>Accepted file types: {accept}</em>
        </div>
      )}
      {maxFileSize && (
        <div className="ds-upload-accept FP_Content">
          <em>Maximum file size: {maxFileSize / (1024 * 1024)} MB</em>
        </div>
      )}
      {isMaxReached && (
        <div className="ds-upload-limit-message FP_Content">
          Maximum {numberLimit} files allowed.
        </div>
      )}
      {!!files?.length && (
        <div style={{ marginTop: 10 }}>
          <UploadedFileList files={files} onRemove={handleRemove} />
        </div>
      )}
    </div>
  );
};

export default FileUpload;
